/**
 * What « / » used to show (Pulse) now lives in the conversation (« Ta journée », components/day-brief.tsx). This module
 * keeps the shared hooks and badges other pages use.
 */
import type { Dashboard } from "../../shared/contracts";
import type { PulseSummary } from "../../shared/pulse-summary";
import { Badge } from "../components/ui";
import { api } from "../lib/api";
import { machineStateLabel, runStatusLabel } from "../lib/format";
import { useResource } from "../lib/store";

export function useDashboard() {
  return useResource<Dashboard>("dashboard", (signal) => api("GET /api/dashboard", { signal }), {
    refreshOn: [
      "brief.created",
      "approval.upsert",
      "run.upsert",
      "mission.upsert",
      "machine.upsert",
      "chat.message",
      "settings.updated",
      "turn.started",
      "turn.done",
    ],
  });
}

/** The generative summary of the day (agent/pulse-summary.ts): refetched when what it is made of changes. */
export function usePulseSummary({ enabled = true }: { enabled?: boolean } = {}) {
  return useResource<PulseSummary>("pulse-summary", (signal) => api("GET /api/pulse/summary", { signal }), {
    refreshOn: ["approval.upsert", "run.upsert", "mission.upsert", "brief.created"],
    enabled,
  });
}

const RUN_TONE: Record<string, "accent" | "warning" | "success" | "danger" | "neutral"> = {
  queued: "neutral",
  running: "accent",
  waiting_approval: "warning",
  waiting_user: "warning",
  done: "success",
  failed: "danger",
  cancelled: "neutral",
};

export function RunStatusBadge({ status }: { status: string }) {
  return <Badge tone={RUN_TONE[status] ?? "neutral"}>{runStatusLabel(status)}</Badge>;
}

const MACHINE_TONE: Record<string, "success" | "neutral" | "accent" | "warning" | "danger"> = {
  running: "success",
  online: "success",
  starting: "accent",
  sleeping: "accent",
  stopping: "accent",
  stopped: "neutral",
  offline: "neutral",
  unavailable: "warning",
};

export function MachineStateBadge({ state }: { state: string }) {
  return <Badge tone={MACHINE_TONE[state] ?? "neutral"}>{machineStateLabel(state)}</Badge>;
}
